import { FolderIcon, CopyIcon, GlobeIcon } from '../Icons';
import { Component, createSignal, For, mergeProps, splitProps } from 'solid-js';
import classNames from 'classnames';
import { isMobile } from '@/utils/isMobileSignal';
import { isMobileWebView } from '@/utils/isMobileWebView';
import { ModalPreview } from '../ModalPreview';
import { getUtmParameters } from '@/utils/utm';
import { WIDGET_TYPE } from '@/types/constants';

export interface SearchResultCardProps {
  title: string;
  description: string;
  category: string;
  url: string;
  onClick?: () => void;
  onCopy?: () => void;
  isPublic?: boolean;
  loading?: boolean;
  highlight?: boolean;
  expanded?: boolean;
  isExternalLink?: boolean;
  openInModal?: boolean;
  widgetType?: WIDGET_TYPE;
}

export const SearchResultCard: Component<SearchResultCardProps> = (props) => {
  const [showModal, setShowModal] = createSignal(false);
  const merged = mergeProps(
    {
      loading: false,
      highlight: false,
      expanded: false,
      isPublic: false,
      url: '#',
    },
    props,
  );
  const [local] = splitProps(merged, [
    'title',
    'description',
    'category',
    'url',
    'onClick',
    'onCopy',
    'isPublic',
    'loading',
    'highlight',
    'expanded',
    'isExternalLink',
    'openInModal',
    'widgetType',
  ]);

  const getUrlWithUtm = (url: string) => {
    return getUtmParameters(url, local.widgetType);
  };

  const handleCopy = async (e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation();

    await navigator.clipboard.writeText(local.url);

    local.onCopy && local.onCopy();
  };

  const handleClick = (e: MouseEvent | KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const urlWithUtm = getUrlWithUtm(local.url);

    // if the url is a link and not an external link, open the modal
    // external links are opened in a new tab as iframes may not work
    if ((local.openInModal && !local.isExternalLink) || isMobileWebView()) {
      setShowModal(true);
    } else {
      window.open(urlWithUtm, '_blank');
    }
    local.onClick && local.onClick();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  };

  // highlight string <b>bold</b> text
  const getHighlightedText = (text: string, expanded: boolean) => {
    const maxLength = expanded ? text.length : 150;
    let truncatedText = text.slice(0, maxLength);
    if (text.length > maxLength) {
      truncatedText = truncatedText.replace(/\s+\S*$/, '') + '...';
    }

    // remove backslash
    const cleanedText = truncatedText.replace(/\\/g, '');
    const parts = cleanedText.split(/(<b>|<\/b>)/g);

    let isBold = false;
    let isIncomplete = false;

    return (
      <>
        <For each={parts}>
          {(part, index) => {
            if (part === '<b>') {
              isBold = true;
              
return null;
            } else if (part === '</b>') {
              isBold = false;
              
return null;
            } else {
              // Check if this is the last part and we're still bold
              if (index() === parts.length - 1 && isBold) {
                isIncomplete = true;
              }

              return (
                <span
                  class={
                    isBold || isIncomplete
                      ? 'font-bold bg-[var(--tw-gradient-to)] text-[hsl(var(--b1)/var(--tw-bg-opacity))] px-0.5'
                      : ''
                  }
                >
                  {part}
                </span>
              );
            }
          }}
        </For>
      </>
    );
  };

  return (
    <article
      class={classNames(
        'group flex flex-col h-full bg-white rounded-md cursor-pointer text-current border relative',
        props.highlight
          ? 'border-primary'
          : 'border-[var(--bfsearch-border-color)]',
        { 'p-4': local.loading },
      )}
    >
      {local.loading ? (
        <>
          <div class="w-24 h-2.5 mb-2.5 bg-gray-300 rounded-full dark:bg-gray-600" />
          <div class="w-32 h-2 bg-gray-200 rounded-full dark:bg-gray-700" />
        </>
      ) : (
        <>
          <a
            href={getUrlWithUtm(local.url)}
            rel="noopener"
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            class="p-4 transition-all duration-100 focus:ring-2 focus:ring-inset"
            target="_blank"
          >
            <header class="flex justify-between items-start mb-2 pr-9">
              <h3 class="font-primary text-[16px] font-bold line-clamp-1">
                {local.title}
              </h3>
            </header>
            <p class="text-sm mb-3">
              {getHighlightedText(local.description, local.expanded || false)}
            </p>
            <footer>
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-[20px] text-sm font-[12px] text-[var(--bfsearch-input-color)] border border-[var(--bfsearch-input-border-color)]">
                {local.isPublic ? (
                  <GlobeIcon class="mr-2" />
                ) : (
                  <FolderIcon class="mr-2" />
                )}
                {local.category}
              </span>
            </footer>
          </a>
          <button
            onClick={handleCopy}
            onKeyDown={(e) => e.key === 'Enter' && handleCopy(e)}
            class="absolute hover:bg-gray-100 size-9 p-2 top-2 right-2 transition-all duration-100 focus:ring-2 focus:ring-inset"
            aria-label="Copy"
          >
            <CopyIcon class="w-5 h-5 text-[var(--bfsearch-input-color)]" />
          </button>
        </>
      )}
      {showModal() && (
        <ModalPreview
          url={(() => {
            const urlWithUtm = getUrlWithUtm(local.url);
            const urlObj = new URL(urlWithUtm);
            urlObj.searchParams.set('onlyBody', 'true');
            
return urlObj.toString();
          })()}
          width="500px"
          height="600px"
          isMobile={isMobile()}
          onClose={() => setShowModal(false)}
        />
      )}
    </article>
  );
};

export const SearchResultCards = ({ children, num, ...props }: any) => {
  // row
  return (
    <div class="grid w-full grid-cols-1 gap-4" {...props}>
      {children}
    </div>
  );
};
